/**
 * TD-2352 — Company data-source categories + static field → source map (v1, frontend-only).
 *
 * Each company-data field is tagged with one of four provenance categories. v1 derives the
 * category from the static maps below (the briefing's field mapping); the one dynamic case is
 * self-disclosed financials, which reuse the existing BE flag (source === 'basic'). v2 (TD-2363)
 * will replace these maps with authoritative per-field metadata from the API — keeping resolution
 * centralised here means that swap stays local to this file.
 */

export const DATA_SOURCE = {
  REGISTRY: 'registry',
  SELF_DISCLOSED: 'selfDisclosed',
  WEBSITE: 'website',
  ENRICHMENT: 'enrichment',
  UNKNOWN: 'unknown'
};

/**
 * Per-category display metadata. Colours live in LESS (headline `.ds-*` rules + the floating
 * legend `.dsl-sw`); `className` builds those class names, `label` is the English legend text
 * (these pages have no i18n framework).
 */
export const DATA_SOURCE_META = {
  [DATA_SOURCE.REGISTRY]: { className: 'reg', label: 'Registry' },
  [DATA_SOURCE.SELF_DISCLOSED]: { className: 'self', label: 'Self-disclosed' },
  [DATA_SOURCE.WEBSITE]: { className: 'web', label: 'Website / documents' },
  [DATA_SOURCE.ENRICHMENT]: { className: 'enr', label: 'Contact enrichment' },
  [DATA_SOURCE.UNKNOWN]: { className: 'unk', label: 'Unknown' }
};

/** Ordered categories shown as the legend key. UNKNOWN is intentionally omitted. */
export const LEGEND_ITEMS = [
  DATA_SOURCE.REGISTRY,
  DATA_SOURCE.SELF_DISCLOSED,
  DATA_SOURCE.WEBSITE,
  DATA_SOURCE.ENRICHMENT
];

/**
 * Static field → source map. Keyed by a semantic field name so call sites read e.g.
 * FIELD_SOURCE.description. Fields absent here get no indicator (unknown → hidden).
 */
export const FIELD_SOURCE = {
  // Company websites / documents & filings — all qualitative fields
  description: DATA_SOURCE.WEBSITE,
  productPortfolio: DATA_SOURCE.WEBSITE,
  industries: DATA_SOURCE.WEBSITE,
  valueChain: DATA_SOURCE.WEBSITE,
  products: DATA_SOURCE.WEBSITE,
  keyProducts: DATA_SOURCE.WEBSITE,
  businessVerticals: DATA_SOURCE.WEBSITE,

  // Registry data
  financials: DATA_SOURCE.REGISTRY,
  shareholders: DATA_SOURCE.REGISTRY,
  subsidiaries: DATA_SOURCE.REGISTRY,
  companyContact: DATA_SOURCE.REGISTRY,
  contacts: DATA_SOURCE.REGISTRY,
  foundingYear: DATA_SOURCE.REGISTRY,
  // The WZ code comes with the Creditreform register record.
  industryCode: DATA_SOURCE.REGISTRY,
  ownership: DATA_SOURCE.REGISTRY,
  hqLocation: DATA_SOURCE.REGISTRY,
  topManagement: DATA_SOURCE.REGISTRY,
  // TD-2510: the legal-identifier rows (commercial register, VAT ID) come
  // straight off the register entry, so they share the registry provenance.
  identifiers: DATA_SOURCE.REGISTRY,

  // Credit-agency data, the same provenance as self-disclosed financials
  banks: DATA_SOURCE.SELF_DISCLOSED,

  // Contact enrichment (contact-data providers)
  personalEmail: DATA_SOURCE.ENRICHMENT,
  mobilePhone: DATA_SOURCE.ENRICHMENT
};

/**
 * Financials are Registry by default, but self-disclosed (credit-agency) figures carry the
 * existing BE flag (`source === 'basic'` → isRevenueBasic / isEmployeesBasic). Reuse it.
 *
 * @param {{ isBasic?: boolean }} [param0]
 * @returns {string} a DATA_SOURCE value
 */
export const resolveFinancialSource = ({ isBasic } = {}) =>
  isBasic ? DATA_SOURCE.SELF_DISCLOSED : DATA_SOURCE.REGISTRY;

/** Tooltip of a sales figure that shows gross profit because sales were not reported. */
export const GROSS_PROFIT_TOOLTIP = 'Gross profit, sales not reported';

/**
 * Headline-coloring class for a data source, e.g. `ds-source ds-reg`. Applied to a section/field
 * HEADLINE; the actual colour only shows when the page carries `body.show-data-sources` (toggle on).
 * Returns '' for unknown/unmapped sources so those headlines stay default (uncoloured).
 */
export const dataSourceClass = (source) => {
  const meta = DATA_SOURCE_META[source];
  if (!meta || source === DATA_SOURCE.UNKNOWN) return '';
  return `ds-source ds-${meta.className}`;
};
